// Révision d'un Word ou d'un PDF fourni par l'usager : la partie légère,
// chargée partout (types, lecture du bloc JSON, conservation du fichier
// d'origine). Le moteur qui réécrit le .docx, avec fflate, est dans
// docxRevise.ts et ne se charge qu'au clic sur « Télécharger » ; un PDF est
// modifié par le serveur (pdfEdit.ts).

export interface DocxEdit {
  find: string;
  replace: string;
}

export interface DocxRevision {
  /** Nom du document d'origine (pièce jointe ou document de la bibliothèque). */
  source: string;
  edits: DocxEdit[];
  /** `"continue": true` : prolonge la révision précédente du même document
   *  dans la conversation, au lieu de repartir de l'original (29.10.2026). */
  continue: boolean;
  /** Révision d'un fichier généré dans la conversation (03.11.2026) : champs
   *  de son en-tête (modèle EPFL, couverture) ou clés de son JSON à changer. */
  fields?: Record<string, string>;
}

export const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Le corps d'un bloc ````file:….docx est-il une révision (JSON) plutôt
 *  qu'un document markdown à créer ? */
export function parseRevision(body: string): DocxRevision | null {
  const text = body.trim();
  if (!text.startsWith("{")) return null;
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  if (!data || typeof data !== "object") return null;
  const { source, edits, continue: cont, champs, fields } = data as Record<string, unknown>;
  const rawFields = champs ?? fields;
  const hasFields = !!rawFields && typeof rawFields === "object" && !Array.isArray(rawFields);
  // Seulement des champs (« change la signature ») : pas de remplacement.
  if (!Array.isArray(edits) && !(hasFields && typeof source === "string")) return null;
  return {
    source: typeof source === "string" ? source : "",
    ...(hasFields
      ? { fields: Object.fromEntries(Object.entries(rawFields as Record<string, unknown>).map(([k, v]) => [k, v == null ? "" : String(v)])) }
      : {}),
    edits: (Array.isArray(edits) ? edits : [])
      .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
      .map((e) => ({ find: String(e.find ?? ""), replace: String(e.replace ?? "") }))
      .filter((e) => e.find.trim() !== ""),
    continue: cont === true,
  };
}

// === Conservation du fichier d'origine =======================================

/** Au-delà, on ne garde pas l'original (base64 dans IndexedDB). */
export const MAX_ORIGINAL_BYTES = 15 * 1024 * 1024;

/** Fichier gardé tel quel pour que le modèle puisse le rendre modifié : un
 *  .docx, ou un PDF qui a une couche texte (api/pdfEdit.ts). Un PDF numérisé,
 *  lu par OCR, n'a pas de texte à remplacer : le modèle le réécrit. */
export function keepsOriginal(file: File, ocrUsed: boolean): boolean {
  if (file.size > MAX_ORIGINAL_BYTES) return false;
  return /\.docx$/i.test(file.name) || (/\.pdf$/i.test(file.name) && !ocrUsed);
}

export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
